import React, {Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {Table, TableBody, TableCell, TableRow} from '@mui/material'
import PPFileInput from '../shared/forms/PPFileInput'
import styled from 'styled-components'
import {AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../styledComponents/adminPage/Links'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea
} from '../shared/hoc/withAdminPageTemplate'
import {Messages} from '../../Messages'
import AdminGeneralMeetingRepo from './AdminGeneralMeetingRepo'
import PPTextField from '../shared/forms/PPTextField'
import {Nullable} from '../../app/TypeHelpers'
import {RequestItemErrors} from '../../__NoAutomatedTests/components/common/repository/NatBaseResponseInterface'
import {HKConstants} from '../../HKConstants'
import {StyledTableCaption, TableRowName} from '../../styledComponents/adminPage/Table'
import {formatNumberWithCommas} from '../../formatters/formatNumberWithCommas'
import {LeftRowFlexContainer} from '../../styledComponents/FlexComponents'

interface Props {
    condominiumCd: string
    adminGeneralMeetingRepo: AdminGeneralMeetingRepo
}

interface State {
    readFile: Nullable<string>
    importErrorMessageText: Nullable<string>
}

class AdminVotingRightsCountBulkUpdatePage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            readFile: null,
            importErrorMessageText: null
        }

        this.handleChangeFile = this.handleChangeFile.bind(this)
        this.handleClickRegisterCancel = this.handleClickRegisterCancel.bind(this)
        this.handleClickRegisterOK = this.handleClickRegisterOK.bind(this)
        this.handleEmitMessage = this.handleEmitMessage.bind(this)
        this.handleClickRegisterButton = this.handleClickRegisterButton.bind(this)
        this.handleClickBackLink = this.handleClickBackLink.bind(this)
    }

    componentDidMount(): void {
        this.props.eventEmitter.on('invalidFile', this.handleEmitMessage)
    }

    componentWillUnmount(): void {
        this.props.eventEmitter.off('invalidFile', this.handleEmitMessage)
    }

    handleChangeFile(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.files && event.target.files[0]) {
            const reader: FileReader = new FileReader()
            reader.onload = () => {
                if (typeof reader.result !== 'string') {
                    throw new Error(`ファイル読み込みで異常発生`)
                }
                this.setState({
                    readFile: reader.result,
                    importErrorMessageText: null,
                })
            }
            reader.readAsDataURL(event.target.files[0])
        } else {
            this.setState({
                readFile: null,
                importErrorMessageText: null,
            })
        }
    }

    handleClickRegisterCancel(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleClickRegisterOK(): Promise<void> {
        const {
            condominiumCd,
            adminGeneralMeetingRepo,
            asyncCallFunction,
            eventEmitter,
        } = this.props

        const {
            readFile,
        } = this.state

        if (readFile == null) {
            throw new Error('ファイルが指定されていません')
        }

        const commaSplitReadFile: string[] = readFile.split(',')

        if (commaSplitReadFile.length !== 2) {
            throw new Error('読み込んだデータが不正')
        }

        eventEmitter.closeDialog()
        await eventEmitter.showLoading()
        try {
            const response = await asyncCallFunction(
                adminGeneralMeetingRepo.bulkUpdateVotingRightsCount.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    {
                        votingRightsBulkUpdateData: commaSplitReadFile[1]
                    },
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('議決権数')},
            )

            if (response === null) {
                return
            }

            this.props.navigate(
                `/admin/condominiums/${condominiumCd}/unit-owners`,
                {
                    state: {
                        informations: response.informations,
                    },
                }
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleEmitMessage(itemError: RequestItemErrors): void {
        this.setState({
            importErrorMessageText: itemError.message,
        })
    }

    async handleClickRegisterButton(): Promise<void> {
        await this.props.eventEmitter.showDialog({
            title: '確認',
            errorLevel: Messages.IOPCC0002.errorLevel,
            message: Messages.IOPCC0002.toString('議決権数'),
            buttonProps: [
                {
                    isDefault: false,
                    buttonLabel: 'キャンセル',
                    buttonHandler: this.handleClickRegisterCancel,
                },
                {
                    isDefault: false,
                    buttonLabel: 'OK',
                    buttonHandler: this.handleClickRegisterOK,
                },
            ],
        })
    }

    handleClickBackLink(): void {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/unit-owners`)
    }

    render(): ReactNode {
        const countLimit = formatNumberWithCommas(HKConstants.VMN.RES.RESIDENCE_USER_IMPORT_CSV_LINE_COUNT_LIMIT)

        return <>
            <PageMainArea>
                <Table>
                    <StyledTableCaption>議決権数の情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName required>議決権数一括更新CSV</TableRowName>
                            <TableCell>
                                <FileInputArea>
                                    <PPFileInput
                                        onChange={this.handleChangeFile}
                                        eventKeys={['votingRightsBulkUpdateData']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                    1ファイル5MB、 データ件数{countLimit}件まで
                                </FileInputArea>
                            </TableCell>
                        </TableRow>
                        {this.state.importErrorMessageText &&
                            <TableRow>
                                <TableRowName>エラー内容</TableRowName>
                                <TableCell>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                readOnly: true,
                                                minRows: 5,
                                                maxRows: 5,
                                            },
                                        }}
                                        fullWidth
                                        multiline
                                        value={this.state.importErrorMessageText}
                                    />
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </PageMainArea>
            <PageFooterArea>
                <PageFotterAreaLeft>
                    <BackLink
                        onClick={this.handleClickBackLink}
                    >
                        区分所有者一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        onClick={this.handleClickRegisterButton}
                        disabled={this.state.readFile === null}
                    >
                        登録する
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
            </PageFooterArea>
        </>
    }
}

export default createAdminPage(AdminVotingRightsCountBulkUpdatePage)

const FileInputArea = styled(LeftRowFlexContainer)`
  gap: 16px;
`
